// components/chat-surface/chat-message-toolbar.js
// ═══════════════════════════════════════════════════════════
// Toolbars for AI and user messages. Appear on hover below message.
// AI: Shift logo (animated during stream) + Copy + Pin + Regenerate
// User: Edit + Copy + Delete
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

// ── Shift logo — animated pulse during streaming, static after ──
function ShiftLogo({ streaming }) {
  return (
    <div style={{
      width: 16, height: 16, borderRadius: '50%',
      background: streaming ? 'rgba(200,169,110,0.15)' : 'rgba(200,169,110,0.08)',
      border: `1px solid ${streaming ? 'rgba(200,169,110,0.4)' : 'rgba(200,169,110,0.2)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 8, color: '#c8a96e', fontFamily: 'Georgia, serif', fontWeight: 700,
      animation: streaming ? 'chat-logo-pulse 1.5s ease-in-out infinite' : 'none',
      transition: 'all 0.3s',
    }}>
      S
    </div>
  );
}

// ── Streaming indicator — three dots ──
export function StreamingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: 2, padding: '4px 0' }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          display: 'inline-block', width: 4, height: 4, borderRadius: '50%',
          background: '#c8a96e',
          animation: `chat-dot-blink 1.2s ${i * 0.2}s infinite`,
        }} />
      ))}
    </span>
  );
}

// ── Toolbar icon button ──
function ToolbarBtn({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  const color = hovered ? (danger ? '#c46050' : '#7a9e82') : '#555';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={label}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color, fontSize: 11, padding: '2px 6px',
        fontFamily: 'Georgia, serif', transition: 'color 0.12s',
        display: 'flex', alignItems: 'center', gap: 4,
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      <span style={{ fontSize: 9 }}>{label}</span>
    </button>
  );
}

// ── AI message toolbar ──
export function AIToolbar({ message, streaming, onCopy, onPin, onRegenerate }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2, marginTop: 4,
    }}>
      <ShiftLogo streaming={streaming} />
      {!streaming && (
        <>
          <ToolbarBtn icon="⎘" label="Copy" onClick={() => {
            try {
              navigator.clipboard.writeText(typeof message.content === 'string' ? message.content : '');
            } catch (_) { /* clipboard not available */ }
            onCopy?.();
          }} />
          {onPin && <ToolbarBtn icon="⊞" label="Pin" onClick={() => onPin(message)} />}
          {onRegenerate && <ToolbarBtn icon="↻" label="Regenerate" onClick={() => onRegenerate(message)} />}
        </>
      )}
    </div>
  );
}

// ── User message toolbar ──
export function UserToolbar({ message, onEdit, onCopy, onDelete }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2, marginTop: 4,
      justifyContent: 'flex-end',
    }}>
      {onEdit && <ToolbarBtn icon="✎" label="Edit" onClick={() => onEdit(message)} />}
      <ToolbarBtn icon="⎘" label="Copy" onClick={() => {
        try {
          navigator.clipboard.writeText(typeof message.content === 'string' ? message.content : '');
        } catch (_) { /* clipboard not available */ }
        onCopy?.();
      }} />
      {onDelete && <ToolbarBtn icon="✕" label="Delete" onClick={() => onDelete(message)} danger />}
    </div>
  );
}

// ── Keyframes — import this component to get the animations ──
export function ChatToolbarStyles() {
  return (
    <style jsx global>{`
      @keyframes chat-logo-pulse {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
      }
      @keyframes chat-dot-blink {
        0%, 100% { opacity: 0.2; }
        50% { opacity: 1; }
      }
    `}</style>
  );
}
